import { useNavigate } from '@tanstack/react-router';
import {
  Activity as ActivityIcon,
  Calendar as CalendarIcon,
  Clock,
  KanbanSquare,
  LayoutList,
  Settings,
  StickyNote,
} from 'lucide-react';
import { SegmentedControl, type SegmentedControlOption } from '@/components/glass';

export type ProjectView =
  | 'list'
  | 'board'
  | 'calendar'
  | 'time'
  | 'notes'
  | 'activity'
  | 'settings';

export interface ProjectViewSwitcherProps {
  workspaceSlug: string;
  projectSlug: string;
  current: ProjectView;
  className?: string;
}

interface ViewDef {
  id: ProjectView;
  label: string;
  Icon: typeof LayoutList;
  to:
    | '/w/$workspaceSlug/p/$projectSlug'
    | '/w/$workspaceSlug/p/$projectSlug/board'
    | '/w/$workspaceSlug/p/$projectSlug/calendar'
    | '/w/$workspaceSlug/p/$projectSlug/time'
    | '/w/$workspaceSlug/p/$projectSlug/notes'
    | '/w/$workspaceSlug/p/$projectSlug/activity'
    | '/w/$workspaceSlug/p/$projectSlug/settings';
}

const VIEWS: ReadonlyArray<ViewDef> = [
  { id: 'list', label: 'List', Icon: LayoutList, to: '/w/$workspaceSlug/p/$projectSlug' },
  { id: 'board', label: 'Board', Icon: KanbanSquare, to: '/w/$workspaceSlug/p/$projectSlug/board' },
  { id: 'calendar', label: 'Calendar', Icon: CalendarIcon, to: '/w/$workspaceSlug/p/$projectSlug/calendar' },
  { id: 'time', label: 'Time', Icon: Clock, to: '/w/$workspaceSlug/p/$projectSlug/time' },
  { id: 'notes', label: 'Notes', Icon: StickyNote, to: '/w/$workspaceSlug/p/$projectSlug/notes' },
  { id: 'activity', label: 'Activity', Icon: ActivityIcon, to: '/w/$workspaceSlug/p/$projectSlug/activity' },
  { id: 'settings', label: 'Settings', Icon: Settings, to: '/w/$workspaceSlug/p/$projectSlug/settings' },
];

/**
 * Glass redesign — Phase 3.
 *
 * Project surface tabs as a `SegmentedControl`. Each segment is an
 * icon-only button (label hidden visually but exposed via aria-label
 * + tooltip on hover via the underlying SegmentedControl). Active
 * segment lifts to the foreground; inactive sit transparent against
 * the topbar.
 *
 * Used inside the topbar (right slot) on every project surface.
 * Navigation goes through `useNavigate` rather than `<Link>` because
 * SegmentedControl is generic-button based, not link-based — but the
 * effect is the same: TanStack Router preloads on hover and the
 * transition is instantaneous.
 */
export const ProjectViewSwitcher = ({
  workspaceSlug,
  projectSlug,
  current,
}: ProjectViewSwitcherProps) => {
  const navigate = useNavigate();

  const options: SegmentedControlOption<ProjectView>[] = VIEWS.map(({ id, label, Icon }) => ({
    value: id,
    icon: <Icon className="size-4" aria-hidden="true" />,
    ariaLabel: label,
  }));

  return (
    <SegmentedControl
      options={options}
      value={current}
      onChange={(next) => {
        const view = VIEWS.find((v) => v.id === next);
        if (!view) return;
        void navigate({ to: view.to, params: { workspaceSlug, projectSlug } });
      }}
      ariaLabel="Project view"
    />
  );
};
